import {inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {
    CreateFenceRequest,
    FenceBackend,
    FenceFilterRequest,
    OrgPolicyBackend, UpdateFenceRequest,
    UpdatePolicyRequest
} from "../models/geo-fence";
import {Observable, retry, throwError} from "rxjs";
import {APIResponse} from "../../../models/Response";
import {catchError} from "rxjs/operators";
import {environment} from "../../../../environments/environment";
import {AssignFenceRequest, AssignFenceResponse, FenceAssignmentsResponse} from "../models/fence-assignment.model";
import {CreatePunchRequest, PunchRequestResponse} from "../models/punch-request.backend.model";

@Injectable({
    providedIn: 'root'
})
export class GeoFenceAPIService {
    private http = inject(HttpClient);
    private readonly baseUrl = environment.apiBaseUrl + '/geo-fence-policy';

    /**
     * Get organization policy (/geo-fence-policy/2/getGeoFencePolicy)
     * @returns Observable of APIResponse containing OrgPolicyBackend
     */
    getOrgPolicy(orgId: number, accountId: string) {
        return this.http.get<APIResponse<OrgPolicyBackend>>(`${this.baseUrl}/${orgId}/getGeoFencePolicy`, {
            headers: {accountIds: accountId}
        }).pipe(
            retry(1), // Retry once on failure
            catchError((error) => throwError(() => error))
        );
    }

    /**
     * Update organization policy (/geo-fence-policy/2/updateGeoFencePolicy)
     * @param orgId
     * @param accountId
     * @param policySet
     * @returns Observable of APIResponse containing any
     */
//   /api/geo-fence-policy/2/updateGeoFencePolicy
    updateOrgPolicy(orgId: number, accountId: string, policySet: UpdatePolicyRequest): Observable<APIResponse<any>> {
        return this.http.post<APIResponse<any>>(`${this.baseUrl}/${orgId}/updateGeoFencePolicy`, policySet, {
            headers: {accountIds: accountId}
        }).pipe(
            catchError((error) => throwError(() => error))
        );
    }

    /**
     * Get all fences with optional filters
     * POST /api/fence/2/getFence
     */
    getFences(filters: FenceFilterRequest, orgId: number, accountId: string): Observable<APIResponse<FenceBackend[]>> {
        // If your API uses POST for search with body
        const requestBody = {
            status: filters?.status || 'both',
            fenceName: filters?.fenceName || undefined,
            siteCode: filters?.siteCode || undefined
        };

        return this.http.post<APIResponse<FenceBackend[]>>(`${environment.apiBaseUrl}/fence/${orgId}/getFence`, requestBody, {
            headers: {accountIds: accountId}
        }).pipe(
            retry(1),
            catchError((error) => throwError(() => error))
        );
    }

    // createFence(request: CreateFenceRequest) {
    //
    // }
    /**
     * Create new fence
     * POST /api/fences
     */
    createFence(orgId: number, accountIds: string, fence: CreateFenceRequest) {
        return this.http.post<APIResponse<FenceBackend>>(`${environment.apiBaseUrl}/fence/${orgId}/createFence`, fence, {
            headers: {accountIds: accountIds}
        }).pipe(
            catchError((error) => throwError(() => error))
        );
    }

    /**
     * Update existing fence
     * PUT /api/fences/:id
     */
    updateFence(orgId: number, accountIds: string, fence: UpdateFenceRequest) {
        return this.http.post<APIResponse<FenceBackend>>(`${environment.apiBaseUrl}/fence/${orgId}/updateFence`, fence, {
            headers: {accountIds: accountIds}
        }).pipe(catchError((error) => throwError(() => error)));
    }

    /**
     * Get assigned and unassigned entities for a fence
     * GET /api/fenceAssignment/{orgId}/getAssignedEntityOfFence?fenceId={fenceId}
     */
    assignmentBaseUrl = environment.apiBaseUrl + '/fenceAssignment';

    getAssignmentsForFence(orgId: number, accountId: string, fenceId: number): Observable<APIResponse<FenceAssignmentsResponse>> {
        return this.http
            .get<APIResponse<FenceAssignmentsResponse>>(`${this.assignmentBaseUrl}/${orgId}/getAssignedEntityOfFence`, {
                params: {fenceId: fenceId.toString()}, headers: {accountIds: accountId}
            })
            .pipe(
                catchError((error) => throwError(() => error))
            );
    }

    /**
     * Assign/Unassign entities to/from fence (batch operation)
     * POST /api/fenceAssignment/{orgId}/assignFenceToEntity
     */
    assignFenceToEntities(
        orgId: number,
        accountId: string,
        request: AssignFenceRequest
    ): Observable<APIResponse<AssignFenceResponse>> {
        return this.http
            .post<APIResponse<AssignFenceResponse>>(`${this.assignmentBaseUrl}/${orgId}/assignFenceToEntity`, request,
                {headers: {accountIds: accountId}}
            ).pipe(
                catchError((error) => throwError(() => error))
            )
    }


    private punchRequestBaseUrl = environment.apiBaseUrl + '/punchRequest';

    /**
     * Request punch for an entity
     * POST /api/punchRequest/{orgId}/requestPunchForEntity
     */
    requestPunchForEntity(
        orgId: number,
        accountId: string,
        request: CreatePunchRequest
    ): Observable<PunchRequestResponse> {
        return this.http
            .post<PunchRequestResponse>(`${this.punchRequestBaseUrl}/${orgId}/requestPunchForEntity`, request,
                {headers: {accountIds: accountId}})
            .pipe(catchError((error) => throwError(() => error)));
    }

    /**
     * Get pending punch request history
     * GET /api/punchRequest/{orgId}/getPendingRequestHistory
     */
    getPendingRequestHistory(
        orgId: number,
        currentUserAccountId: string,
        accountIds: string[],
        from: string,
        to: string
    ): Observable<APIResponse<PunchRequestResponse[]>> {
        const params = {
            accountId: accountIds.join(','),
            from,
            to
        };
        return this.http
            .get<APIResponse<PunchRequestResponse[]>>(`${this.punchRequestBaseUrl}/${orgId}/getPendingRequestHistory`, {
                params,
                headers: {accountIds: currentUserAccountId}
            })
            .pipe(catchError((error) => throwError(() => error)));
    }

    /**
     * Get a single punch request by its id
     * GET /api/punchRequest/{orgId}/getPunchRequestById?id={id}
     */
    getPunchRequestById(
        orgId: number,
        id: number,
        accountId: string
    ): Observable<APIResponse<PunchRequestResponse>> {
        return this.http
            .get<APIResponse<PunchRequestResponse>>(`${this.punchRequestBaseUrl}/${orgId}/getPunchRequestById`, {
                params: { id: id.toString() },
                headers: { accountIds: accountId }
            })
            .pipe(catchError((error) => throwError(() => error)));
    }
}
